import { Link } from "react-router-dom";
import { ArrowRight, ChefHat, DollarSign, Users, ScrollText, Scale } from "lucide-react";
import FaqSchema from "@/components/FaqSchema";

// Landing page for the Kitchen Operations & Formulas resource hub.
// Lists the four sub topics with a one-line summary and a card CTA.

const RESOURCES = [
  {
    slug: "food-cost",
    title: "Food Cost",
    lede: "Standard formulas, edible yield tables, and the plain-language method every station line cook can memorise before service.",
    icon: DollarSign,
    href: "/resources/food-cost",
  },
  {
    slug: "labour-cost",
    title: "Labour Cost",
    lede: "Fully loaded hourly rates, labour cost benchmarks by service style, and the five rota formulas that keep a kitchen profitable.",
    icon: Users,
    href: "/resources/labour-cost",
  },
  {
    slug: "recipe-formulas",
    title: "Recipe Formulas",
    lede: "Baker's percentage, mother sauce ratios, brine strengths, and pastry formulas that let a station scale a plate without losing consistency.",
    icon: ScrollText,
    href: "/resources/recipe-formulas",
  },
  {
    slug: "bulk-conversion",
    title: "Bulk Conversion",
    lede: "Ingredient density charts, weight to volume conversions, hotel pan yields, and the rules that break when a recipe goes production scale.",
    icon: Scale,
    href: "/resources/bulk-conversion",
  },
];

const HUB_FAQS = [
  {
    q: "What is the Kitchen Operations and Formulas resource hub?",
    a: "It is a free, non-gated reference library of the formulas, ratios, and target percentages that professional kitchens use every day. Each page starts with a plain-language 50 word answer, then supplies the tables a chef would print and pin to a station wall.",
  },
  {
    q: "Do I need a Culinaire Specialist account to read the resource pages?",
    a: "No. Every resource page is public and free. The pages exist as an open reference for the industry. If you want the same formulas applied live to your own kitchen data, our software is a free trial away.",
  },
  {
    q: "How often are the formulas updated?",
    a: "The ratios and formulas themselves are anchored in classical technique and rarely change. Benchmark ranges for food cost and labour cost are reviewed each quarter against current supplier and wage data across Australia, the UK, and North America.",
  },
];

export default function ResourceHub() {
  return (
    <div className="min-h-screen">
      <FaqSchema
        id="resources-hub"
        title="Kitchen Operations and Formulas: The Chef's Reference | Culinaire Specialist"
        description="Free reference library covering food cost, labour cost, recipe formulas, and bulk conversion. Standard tables, ratios, and benchmarks for professional kitchens."
        canonicalUrl="https://www.culinairespecialist.com/resources"
        faqs={HUB_FAQS}
      />

      <header className="border-b border-border/60">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/brand/mark-256.png"
              alt="Culinaire Specialist"
              className="w-9 h-9 rounded-full object-contain"
              style={{ background: "#0a0806", boxShadow: "0 0 0 1px rgba(232,207,154,0.28)" }}
            />
            <span className="font-display text-lg">Culinaire Specialist</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/blog" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/70">Journal</Link>
            <Link to="/resources" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/90 font-medium">Resources</Link>
            <Link to="/pricing" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/70">Pricing</Link>
            <Link to="/register" className="ml-2 gold-cta px-4 h-9 rounded-full inline-flex items-center gap-1.5 font-medium text-sm">
              Start free
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="label-eyebrow flex items-center gap-2 mb-4">
          <ChefHat className="w-3.5 h-3.5" /> The chef's reference
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
          Kitchen Operations
          <br />
          and Formulas.
        </h1>
        <p className="mt-5 text-lg text-white/75 max-w-3xl">
          Four working reference pages covering the numbers that keep a professional kitchen honest. Free, non-gated, and written by chefs. Print them, laminate them, pin them by the pass. Then, when you are ready, let the software do the arithmetic for you.
        </p>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {RESOURCES.map(({ slug, title, lede, icon: Icon, href }) => (
            <Link
              key={slug}
              to={href}
              data-testid={`resource-card-${slug}`}
              className="group rounded-2xl glass-card p-6 flex flex-col gap-3 card-hover transition-colors"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                  style={{
                    background: "linear-gradient(180deg, rgba(232,207,154,0.20), rgba(232,207,154,0.05))",
                    border: "1px solid rgba(232,207,154,0.35)",
                    color: "#f2dfb5",
                  }}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <h2 className="font-display text-2xl tracking-tight">{title}</h2>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">{lede}</p>
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm gold">
                Open the reference
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        <section className="mt-16">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Frequently asked</h2>
          <div className="mt-6 space-y-6">
            {HUB_FAQS.map((f, i) => (
              <div key={i}>
                <div className="font-display text-lg text-white/90">{f.q}</div>
                <p className="mt-2 text-white/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 mt-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 text-sm text-white/50 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Culinaire Specialist. Fine dining kitchen operations, built for chefs.</div>
          <Link to="/register" className="text-white/70 hover:text-white">Start your kitchen</Link>
        </div>
      </footer>
    </div>
  );
}
